// ============================================================================
// modules/usuarios/utils/arbolOrganizacion.ts
// Funciones puras del árbol Compañía > Sucursal > Sede (modal "Accesos por
// organización"). Sin estado ni llamadas a la API.
// ============================================================================
import type { CompaniaArbol, SedeArbol, SucursalArbol } from "../types/accesosOrganizacion.types"

const texto = (v: unknown) => `${v ?? ""}`.trim()

/**
 * Árbol del modo "según grupo" con EXACTAMENTE las sedes que ofrecía el picker
 * histórico (`listaSedes`), agrupadas por su sucursal (`sucursal_cod`).
 */
export const arbolDesdeSedesLegado = (
    listaSedes: any[],
    compania: { codigo: string; nombre: string },
    sucursalPorDefecto: { codigo: string; nombre: string },
): CompaniaArbol[] => {
    const sucursales = new Map<string, SucursalArbol>()
    for (const s of listaSedes || []) {
        const codigo = texto(s?.sucursal_cod) || texto(s?.CompCod) || sucursalPorDefecto.codigo || "-"
        const nombreSuc = texto(s?.sucursal_nom) || (codigo === sucursalPorDefecto.codigo ? sucursalPorDefecto.nombre : codigo)
        if (!sucursales.has(codigo)) sucursales.set(codigo, { codigo, nombre: nombreSuc, estado: "A", sedes: [] })
        const prefijo = `${nombreSuc} — `
        const nombre = texto(s?.SedDesc).startsWith(prefijo) ? texto(s?.SedDesc).slice(prefijo.length) : texto(s?.SedDesc)
        sucursales.get(codigo)!.sedes.push({ codigo: Number(s?.SedCod), nombre, estado: texto(s?.SedEst) || "A" })
    }
    if (sucursales.size === 0) return []
    return [{ codigo: compania.codigo, nombre: compania.nombre, estado: "A", sucursales: [...sucursales.values()] }]
}

export const sedesDeSucursal = (s: SucursalArbol): number[] => s.sedes.map((x) => x.codigo)

export const sedesDeCompania = (c: CompaniaArbol): number[] => c.sucursales.flatMap(sedesDeSucursal)

export const sedesDeArbol = (arbol: CompaniaArbol[]): number[] => arbol.flatMap(sedesDeCompania)

/** "todos" | "algunos" | "ninguno" de un grupo de sedes respecto a la selección. */
export const estadoGrupo = (codigos: number[], seleccion: Set<number>) => {
    const marcadas = codigos.filter((c) => seleccion.has(c)).length
    if (marcadas === 0) return "ninguno"
    return marcadas === codigos.length ? "todos" : "algunos"
}

/** Filtra por texto en compañía, sucursal o sede (conserva la rama que coincide). */
export const filtrarArbol = (arbol: CompaniaArbol[], busqueda: string): CompaniaArbol[] => {
    const q = busqueda.trim().toLowerCase()
    if (!q) return arbol
    const coincide = (v: string) => v.toLowerCase().includes(q)
    return arbol.flatMap((c) => {
        if (coincide(c.nombre)) return [c]
        const sucursales = c.sucursales.flatMap((s) => {
            if (coincide(s.nombre)) return [s]
            const sedes = s.sedes.filter((x) => coincide(x.nombre))
            return sedes.length ? [{ ...s, sedes }] : []
        })
        return sucursales.length ? [{ ...c, sucursales }] : []
    })
}

/** Índice sede -> {sede, sucursal, compañía} para etiquetas y resumen. */
export const indexarSedes = (arbol: CompaniaArbol[]) => {
    const indice = new Map<number, { sede: SedeArbol; sucursal: SucursalArbol; compania: CompaniaArbol }>()
    for (const compania of arbol) {
        for (const sucursal of compania.sucursales) {
            for (const sede of sucursal.sedes) indice.set(sede.codigo, { sede, sucursal, compania })
        }
    }
    return indice
}

/** Etiqueta "SUCURSAL — SEDE" (mismo formato que el picker histórico). */
export const etiquetaSede = (indice: ReturnType<typeof indexarSedes>, codigo: number, respaldo?: string) => {
    const nodo = indice.get(codigo)
    return nodo ? `${nodo.sucursal.nombre} — ${nodo.sede.nombre}` : respaldo || `Sede ${codigo}`
}
